import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getMenuItems } from '../modules/menu';
import { usePublicConfig } from '../hooks/usePublicConfig';
import { queryClient } from '../lib/queryClient';
import { wordpressKeys } from '../hooks/useWordPress';
import { getPageBySlug } from '../api/client';
import { ThemeToggle } from '../themes';
import { BrandLockup } from './BrandLockup';
import { ExternalGiveLink } from './ExternalGiveLink';

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { data: publicConfig } = usePublicConfig();
  const menuItems = getMenuItems(publicConfig?.modules, publicConfig?.menu);
  const isHomePage = location.pathname === '/';

  // Compact bar once the page scrolls (owner request 2026-10-29). Two thresholds (on past
  // 48px, off under 8px) so the 16px height change can't flip the state back and forth.
  useEffect(() => {
    const onScroll = () =>
      setIsScrolled((was) => (was ? window.scrollY > 8 : window.scrollY > 48));
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Hash link detection for <a href> vs <Link to>
  const isHashLink = (path: string) => path.startsWith('/#') || path.startsWith('#');

  // Active link detection
  const isActive = (path: string) => {
    if (path === '/' || path === '') return location.pathname === '/';
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return location.pathname === normalized;
  };

  // Prefetch WordPress page data on hover for instant navigation
  const handlePrefetch = useCallback((path: string) => {
    const slug = path.replace(/^\//, '') || 'home';
    queryClient.prefetchQuery({
      queryKey: wordpressKeys.page(slug),
      queryFn: () => getPageBySlug(slug),
    });
  }, []);

  // Header styling: the frosted light header on every page, Home included, so the
  // full-color logo always shows (client review R1, 2026-10-26; PRP-16 D1).
  // Scrolled: thinner and a little more see-through (the blur keeps text legible).
  const headerClasses = `backdrop-blur-md border-b border-border ${
    isScrolled && !isMobileMenuOpen ? 'bg-surface/75 shadow-sm' : 'bg-surface/90'
  }`;
  const linkColor = 'text-body hover:text-primary';
  const activeLinkColor = 'text-primary';
  // Desktop nav underline grows from the left on hover and stays on the active route.
  const underline = 'after:bg-primary';

  const renderNavLink = (item: { title: string; path: string; external?: boolean }, mobile = false) => {
    const active = isActive(item.path);
    const baseClasses = mobile
      ? `block text-base font-semibold transition-colors ${active ? 'text-primary' : 'text-body hover:text-primary'}`
      : `relative text-sm font-semibold transition-colors ${active ? activeLinkColor : linkColor} after:absolute after:left-0 after:-bottom-1.5 after:h-0.5 after:w-full after:origin-left after:rounded-full after:transition-transform after:duration-300 ${underline} ${active ? 'after:scale-x-100' : 'after:scale-x-0 hover:after:scale-x-100'}`;

    if (item.external) {
      return (
        <a
          key={item.path}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={baseClasses}
        >
          {item.title}
        </a>
      );
    }

    if (isHashLink(item.path)) {
      const handleHashClick = (e: React.MouseEvent) => {
        if (mobile) setIsMobileMenuOpen(false);
        const hash = item.path.includes('#') ? item.path.split('#')[1] : '';
        if (!isHomePage) {
          e.preventDefault();
          navigate('/');
          // Poll for the anchor element after home page renders
          const maxAttempts = 20;
          let attempt = 0;
          const interval = setInterval(() => {
            const el = document.getElementById(hash);
            attempt++;
            if (el) {
              clearInterval(interval);
              el.scrollIntoView({ behavior: 'smooth' });
            } else if (attempt >= maxAttempts) {
              clearInterval(interval);
            }
          }, 100);
        }
      };
      return (
        <a
          key={item.path}
          href={item.path}
          className={baseClasses}
          onClick={handleHashClick}
        >
          {item.title}
        </a>
      );
    }

    const to = item.path.startsWith('/') ? item.path : `/${item.path}`;
    return (
      <Link key={item.path} to={to} className={baseClasses} onMouseEnter={() => handlePrefetch(to)}>
        {item.title}
      </Link>
    );
  };

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${headerClasses}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center transition-[height] duration-300 ${isScrolled ? 'h-16' : 'h-20'}`}>

          {/* Logo: freshSTART CDC lockup */}
          <Link to="/" className="flex items-center" aria-label="freshSTART CDC home">
            <BrandLockup size={isScrolled ? 'sm' : 'md'} className="transition-[height] duration-300" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {menuItems.map(item => renderNavLink(item))}
          </nav>

          {/* CTA + theme toggle + mobile hamburger */}
          <div className="flex items-center gap-4">
            <ThemeToggle className="text-body" />
            <span className="hidden md:inline-flex" data-magnetic>
              <ExternalGiveLink className="btn btn-lime shadow-lg">
                Donate
              </ExternalGiveLink>
            </span>
            <button
              className="md:hidden p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6 transition-colors text-body" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6 transition-colors text-body" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Scroll hairline: a brand gradient whose lime bar fills with page scroll
          (static where scroll timelines are unsupported). */}
      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-primary via-accent to-primary" aria-hidden="true">
        <div className="scroll-progress h-full bg-accent" />
      </div>

      {/* Mobile menu panel: always rendered so it can animate; inert while closed. */}
      <div
        id="mobile-menu"
        data-open={isMobileMenuOpen || undefined}
        // React 18 has no typed `inert` prop; an empty string sets the attribute.
        {...{ inert: isMobileMenuOpen ? undefined : '' }}
        className={`md:hidden absolute inset-x-0 top-full bg-surface border-t border-border px-4 py-6 space-y-4 shadow-lg transition-[opacity,transform,visibility] duration-200 ${
          isMobileMenuOpen ? 'visible opacity-100 translate-y-0' : 'invisible opacity-0 -translate-y-2'
        }`}
      >
        {menuItems.map((item, i) => (
          <div
            key={item.path}
            className={isMobileMenuOpen ? 'animate-fade-in-up' : undefined}
            style={isMobileMenuOpen ? { animationDelay: `${i * 40}ms`, animationFillMode: 'both' } : undefined}
          >
            {renderNavLink(item, true)}
          </div>
        ))}
        <ExternalGiveLink
          className="btn btn-lime w-full mt-4"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          Donate
        </ExternalGiveLink>
      </div>
    </header>
  );
};
